import LoaderCard from '@/components/loader-card/LoaderCard';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { Options } from '@/components/mi-dialog/MiDialog';
import debounce from 'debounce';
import { User } from '@/models/User';
import { RequestCreatePayload } from '@/views/Requests/RequestsTab/RequestsTab';
import { BoxRequest } from '@/models/BoxRequest';
import i18n from '@/i18n';

@Component<RequestDialogCreate>({
  components: {
    'loader-card': LoaderCard,
  }
})
export default class RequestDialogCreate extends Vue {
  private contacts: User[] = [];
  private selectedContact: User | null = null;
  private contactSearch: string = '';
  private debouncedSearch: Function = debounce(this.getContacts, 300);
  private isLoading: boolean = false;
  private inProgress: BoxRequest | null = null;

  //
  // Methods
  //
  private mounted(): void {
    this.initialize();
  }

  private initialize(): void {
    this.getContacts();
  }

  private reset(): void {
    this.selectedContact = null;
  }

  private close(): void {
    this.reset();
    this.$emit('input', false);
  }

  private handleCreateClick(): void {
    if (this.isDisabled) { return; }
    this.$store.dispatch('openDialog', this.createConfirmDialog);
  }

  private getFilters(search: string): ListUserFilters {
    return {
      role: 'contact',
      search: search,
    };
  }

  private async getContacts(search: string = ''): Promise<void> {
    const users: User[] = await new User()
      .include(['profile'])
      .filter(this.getFilters(search))
      .all()
      .catch((error: AxiosError): User[] => {
        ErrorHandler.network(error);
        return [];
      });
    this.contacts = users;
  }

  private async createRequest(): Promise<void> {
    if (!this.selectedContact || !this.selectedContact.id) { return; }
    this.isLoading = true;

    // Check if we have in progress already
    this.inProgress = null;
    const inProgress: BoxRequest | null = await new BoxRequest()
      .getInProgress(this.selectedContact.id);

    if (inProgress && inProgress.id) {
      this.inProgress = inProgress;
      this.$store.dispatch('openDialog', this.createBoxRequestInProgressDialog);

      return;
    }


    // Create
    const request: BoxRequest = await new BoxRequest()
      .create(this.requestCreatePayload)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    if (!request) {
      this.isLoading = false;
      return;
    }
    this.$router.push(`/aanvragen/${request.id}`);
    this.isLoading = false;
  }

  private get requestCreatePayload(): RequestCreatePayload {
    if (!this.selectedContact || !this.selectedContact.id) { return {}; }
    return {
      user: this.selectedContact.id,
    };
  }

  private get isDisabled(): boolean {
    return !this.selectedContact || !this.selectedContact.id;
  }

  private get createConfirmDialog(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: `Weet je zeker dat je een nieuwe aanvraag wil aanmaken voor ${this.selectedContact && this.selectedContact.name ? this.selectedContact.name : ''}?`,
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Ja, aanvraag aanmaken',
          action: (): void => {
            this.createRequest();
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        }
      }
    };
  }

  private get createBoxRequestInProgressDialog(): Options {
    return {
      title: i18n.tc('dialogOptions.existingOrderFound').toString(),
      text: i18n.tc('dialogOptions.existingOrderFoundForClient').toString(),
      type: 'warning',
      buttons: {
        confirm: {
          text: i18n.tc('dialogOptions.showOrder').toString(),
          action: (): void => {
            if (this.inProgress) {
              window.location.href = `/aanvragen/${this.inProgress.id}`;
            }
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        }
      }
    };
  }

  @Watch('contactSearch')
  private searchChanged(query: string): void {
    this.debouncedSearch(query);
  }
}

type ListUserFilters = {
  role?: string;
  search?: string;
}
